import moment, { type Moment } from "moment"
import { v4 as uuidv4 } from "uuid"
import { type IBase } from "@/interfaces/api/base.interface"
import { clone, cloneDeep } from "lodash-es"

export abstract class BaseModel implements IBase {
  public static className: string // Used for socket communication.

  public static verboseName: string // Used for displaying model name in the UI.

  // Subclasses can extend this array with their own fields that should be ignored during ModelManager updates.
  public static updateIgnoreFields: string[] = ["localCreatedAt"] // localCreatedAt should never be updated from the server.

  // Fields that may arrive as UUID strings and should be resolved from ModelManager when managed.
  // Scalar fields and array fields are auto-detected at runtime.
  public static managedResolveFields: string[] = []

  protected static extendUpdateIgnoreFields(fields: string[]): string[] {
    // runtime value is fine, but don't let TS infer too much
    const parent = Object.getPrototypeOf(this) as typeof BaseModel
    const parentFields: string[] = (parent as any).updateIgnoreFields ?? []
    return [...parentFields, ...fields]
  }

  id?: string

  // Server-declared shallowness. Serializers that omit a model's relations emit
  // `shallow: true`; full serializers leave it off entirely, so a hydrated model
  // reads as false. See ShallowIncomingInvoiceSerializer.
  shallow: boolean

  createdAt?: Moment

  updatedAt?: Moment

  // Local creation time.
  localCreatedAt: Moment

  // Whether the model is saved to the server.
  isSaved?: boolean

  // Whether the model is managed by ModelManager.
  isManaged?: boolean = false

  constructor(base: IBase) {
    this.id = base.id ? base.id : uuidv4()

    this.shallow = base.shallow ?? false

    this.createdAt = base.createdAt
    this.updatedAt = base.updatedAt

    this.localCreatedAt = moment()
    this.isSaved = base.isSaved || false
  }

  public getClassName() {
    return (this.constructor as typeof BaseModel).className
  }

  public getVerboseName() {
    return (this.constructor as typeof BaseModel).verboseName
  }

  public clone() {
    const cloneObj = clone(this)
    cloneObj.id = uuidv4()

    delete cloneObj.createdAt
    delete cloneObj.updatedAt
    return cloneObj
  }

  public copy() {
    return cloneDeep(this)
  }

  public isShallow(): boolean {
    // Prefer what the server said. A serializer that OMITS its relations leaves nothing
    // on the payload to infer shallowness from, so it declares it outright.
    if (this.shallow) {
      return true
    }

    // Otherwise a model is "shallow" when one of its declared relations is still an
    // unresolved uuid string (or an array containing them) instead of a hydrated model.
    // Resolve it before relying on nested data. Replaces the old `sid.startsWith("Shallow")`
    // check now that `sid` is gone: if the value is a string, wait until it is an object.
    const relationFields = (this.constructor as typeof BaseModel).managedResolveFields
    return relationFields.some((field) => {
      const value = (this as unknown as Record<string, unknown>)[field]
      return (
        typeof value === "string" ||
        (Array.isArray(value) && value.some((v) => typeof v === "string"))
      )
    })
  }

  /**
   * Promise that resolves once all managed resolve fields on this model instance have been resolved.
   * Null when the model has no pending resolution (unmanaged or no managed resolve fields).
   */
  public whenResolved: Promise<void> | null = null

  /**
   * Awaits the resolution of managed fields on one or more model instances, recursively.
   *
   * Awaits each model's own `whenResolved` first (so its managed UUID fields have been
   * swapped to model instances), then calls itself on every BaseModel / BaseModel[]
   * child reachable through its direct fields. Continues transitively until the full
   * subtree is settled.
   *
   * Resolves immediately if no models have any pending resolutions.
   *
   * @param modelOrModels - A single model or array of models to fully resolve.
   * @param visited - Internal. Shared cycle-guard used when the function recurses
   *   into itself. Callers should not pass this; the default `new WeakSet()` is
   *   reserved for top-level invocations.
   */
  public static async whenFullyResolved(
    modelOrModels: BaseModel | BaseModel[],
    visited: WeakSet<BaseModel> = new WeakSet()
  ): Promise<void> {
    if (Array.isArray(modelOrModels)) {
      await Promise.all(modelOrModels.map((m) => BaseModel.whenFullyResolved(m, visited)))
      return
    }

    const model = modelOrModels
    if (visited.has(model)) {
      return
    }
    visited.add(model)

    // Await this model's own managed-field resolution first. After this await,
    // any UUID strings in its managedResolveFields have been replaced by BaseModel
    // instances, so enumerating fields below will not trip dev warning getters.
    if (model.whenResolved) {
      await model.whenResolved
    }

    // Recurse into direct BaseModel / BaseModel[] children.
    const children: BaseModel[] = []
    for (const key of Object.keys(model)) {
      const value = (model as any)[key]
      if (value instanceof BaseModel) {
        children.push(value)
      } else if (Array.isArray(value)) {
        for (const item of value) {
          if (item instanceof BaseModel) {
            children.push(item)
          }
        }
      }
    }
    if (children.length > 0) {
      await BaseModel.whenFullyResolved(children, visited)
    }
  }
}
